/**
 * @file usePickingQueue.ts
 * @description Данные очереди заданий на сборку (phase19.14, экран A шага 0).
 *
 * Фильтрация вкладок — на клиенте по одному ответу сервера: счётчики во
 * вкладках должны быть живыми, а три запроса ради трёх чисел на складском
 * Wi-Fi дороже, чем фильтр по 200 строкам (потолок очереди на сервере).
 *
 * Сеть на складе пропадает регулярно — состояние `offline` и время последней
 * успешной загрузки живут здесь же: страница только показывает полосу.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  pickingApi,
  type PickingTaskListItemDto,
} from "../../../lib/api-picking";
import { warehousesApi, type WarehouseDto } from "../../../lib/api-wms";
import { wmsErrorText } from "../../../lib/wms-error-text";

/** Вкладки очереди. */
export type QueueTab = "mine" | "all" | "shortage";

/**
 * Значение `?tab=` из адреса; всё неизвестное — «Все».
 *
 * Параметр читается только при первом рендере: вкладку выбирают на самом
 * экране, а в URL её кладёт ссылка извне — плитка «Недостача» с дашборда
 * (phase19.24 этап 1, шаг 3).
 */
export function parseQueueTab(raw: string | null): QueueTab {
  return raw === "mine" || raw === "shortage" ? raw : "all";
}

export function usePickingQueue(
  userId: string | null,
  initialTab: QueueTab = "all",
  /**
   * Склад при открытии: приходит из `?wh=` — плитки дашборда ведут в очередь
   * с тем же складом, который был выбран на сводке (правка по ревью
   * 2026-09-20). Дальше фильтром управляет сам экран.
   */
  initialWarehouseId = "",
) {
  const [tasks, setTasks] = useState<PickingTaskListItemDto[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [tab, setTab] = useState<QueueTab>(initialTab);
  const [warehouseId, setWarehouseId] = useState<string>(initialWarehouseId);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** Момент последней успешной загрузки — подпись жёлтой полосы offline. */
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [offline, setOffline] = useState(!navigator.onLine);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setTasks(await pickingApi.list());
      setLoadedAt(new Date());
      setError(null);
    } catch (err) {
      setError(wmsErrorText(err, "Не удалось загрузить задания на сборку"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    warehousesApi
      .list()
      .then(setWarehouses)
      .catch(() => setWarehouses([]));
  }, [refresh]);

  useEffect(() => {
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const byWarehouse = useMemo(
    () =>
      warehouseId ? tasks.filter((t) => t.warehouse.id === warehouseId) : tasks,
    [tasks, warehouseId],
  );

  const counts = useMemo(
    () => ({
      mine: byWarehouse.filter((t) => t.assignee?.id === userId).length,
      all: byWarehouse.length,
      shortage: byWarehouse.filter((t) => t.status === "SHORTAGE").length,
    }),
    [byWarehouse, userId],
  );

  const visible = useMemo(() => {
    const byTab = byWarehouse.filter((t) => {
      if (tab === "mine") return t.assignee?.id === userId;
      if (tab === "shortage") return t.status === "SHORTAGE";
      return true;
    });
    const needle = search.trim().toUpperCase();
    if (!needle) return byTab;
    return byTab.filter(
      (t) =>
        t.orderNumber.toUpperCase().includes(needle) ||
        t.number.toUpperCase().includes(needle),
    );
  }, [byWarehouse, tab, userId, search]);

  return {
    warehouses,
    tab,
    setTab,
    warehouseId,
    setWarehouseId,
    search,
    setSearch,
    loading,
    error,
    loadedAt,
    offline,
    counts,
    visible,
    refresh,
  };
}
